import type { ReactElement } from 'react';
import { MoreHorizontal } from 'lucide-react';
import type { AutomationButtonRunReceipt, AutomationCommandListItem } from '@molt/shared';
import { Button, IconButton, Menu, Notice } from '@/components';
import {
  useInterfaceLanguage,
  type InterfaceLanguageContextValue,
} from '@/lib/interface-language';

export interface AutomationButtonStripProps {
  label: string;
  buttons: readonly AutomationCommandListItem[];
  maxVisibleButtons?: number | undefined;
  loading?: boolean | undefined;
  disabled?: boolean | undefined;
  disabledReason?: string | undefined;
  runBusyId?: string | null | undefined;
  receipt?: AutomationButtonRunReceipt | null | undefined;
  error?: string | undefined;
  onRun: (commandId: string, options?: { confirmDestructive?: boolean }) => void;
}

function outcomeText(
  t: InterfaceLanguageContextValue['t'],
  receipt: AutomationButtonRunReceipt,
): string {
  const outcome = receipt.run.outcome ?? 'recorded';
  const failed = receipt.actions.filter((action) => !action.succeeded);
  if (receipt.run.detailText !== null) return receipt.run.detailText;
  if (failed.length > 0) {
    return t(
      'primitives.automationButtonStrip.failedActions',
      { count: failed.length },
      failed.length,
    );
  }
  return t('primitives.automationButtonStrip.outcome', { outcome });
}

function failedActionText(receipt: AutomationButtonRunReceipt): string | null {
  const failed = receipt.actions.find((action) => !action.succeeded);
  return failed?.errorText ?? null;
}

export function AutomationButtonStrip({
  label,
  buttons,
  maxVisibleButtons,
  loading = false,
  disabled = false,
  disabledReason,
  runBusyId = null,
  receipt = null,
  error,
  onRun,
}: AutomationButtonStripProps): ReactElement | null {
  const { t } = useInterfaceLanguage();

  if (!loading && buttons.length === 0 && receipt === null && error === undefined) {
    return null;
  }

  const failedAction = receipt === null ? null : failedActionText(receipt);
  const visibleCount =
    maxVisibleButtons === undefined
      ? buttons.length
      : Math.max(0, Math.floor(maxVisibleButtons));
  const visibleButtons = buttons.slice(0, visibleCount);
  const overflowButtons = buttons.slice(visibleCount);

  function disabledReasonFor(button: AutomationCommandListItem): string | undefined {
    return disabled
      ? disabledReason
      : button.isBroken
        ? t('primitives.automationButtonStrip.brokenReason')
        : !button.enabled
          ? t('primitives.automationButtonStrip.disabledReason')
          : undefined;
  }

  return (
    <section className="flex flex-col gap-2" aria-label={label}>
      {loading ? (
        <Notice tone="info" body={t('primitives.automationButtonStrip.loading')} />
      ) : null}
      {buttons.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {visibleButtons.map((button) => (
            <Button
              key={button.id}
              type="button"
              size="sm"
              variant="secondary"
              iconStart={
                button.icon === null ? undefined : (
                  <span className="text-xs leading-none">{button.icon}</span>
                )
              }
              disabled={disabled || button.isBroken || !button.enabled}
              disabledReason={disabledReasonFor(button)}
              busy={runBusyId === button.id}
              onClick={() => onRun(button.id)}
            >
              {button.name}
            </Button>
          ))}
          {overflowButtons.length > 0 ? (
            <Menu
              align="end"
              trigger={
                <IconButton
                  label={t('primitives.automationButtonStrip.overflow', {
                    label: label.toLowerCase(),
                  })}
                  size="sm"
                  disabled={disabled}
                >
                  <MoreHorizontal aria-hidden="true" className="size-4" />
                </IconButton>
              }
              items={overflowButtons.map((button) => ({
                id: button.id,
                label:
                  button.icon === null ? (
                    button.name
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      <span aria-hidden="true">{button.icon}</span>
                      <span>{button.name}</span>
                    </span>
                  ),
                disabled: button.isBroken || !button.enabled,
                onSelect: () => onRun(button.id),
              }))}
            />
          ) : null}
        </div>
      ) : null}
      {receipt !== null ? (
        <Notice
          tone={
            receipt.run.outcome === 'failed'
              ? 'bad'
              : receipt.run.outcome === 'warning'
                ? 'warn'
                : 'info'
          }
          body={outcomeText(t, receipt)}
        />
      ) : null}
      {failedAction !== null ? <Notice tone="bad" body={failedAction} /> : null}
      {error !== undefined ? <Notice tone="bad" body={error} /> : null}
    </section>
  );
}
